import type { ContractorGetCampaignModel } from 'entities/campaign';
import { AlertBox } from 'shared/ui/AlertBox';
import { useContractorDateWithTimeZone } from 'shared/lib/timezones';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';

import { getAlertTextContractor, OverbudgetAlert, BUDGET_SPENT_THRESHOLD_80 } from './Overbudget';

export interface ContractorCampaignDetailsAlertBoxesProps {
  campaign: ContractorGetCampaignModel;
}

export function ContractorCampaignDetailsAlertBoxes({ campaign }: ContractorCampaignDetailsAlertBoxesProps) {
  const { type, status, isActive, spentToBudgetPercent, pendingChanges, campaignEditTimeRestriction } = campaign;
  const isSales = type === 'sales';
  const showDeactivatedAlert = status === 'deactivated';
  const getPendingDate = useContractorDateWithTimeZone();
  const formattedAppliesAt = pendingChanges && getPendingDate(pendingChanges.appliesAt);
  const alertText = getAlertTextContractor({
    status,
    campaignEditRestriction: campaignEditTimeRestriction,
    spentToBudgetPercent,
  });
  const showRunningAlert = status === 'running' && !isActive && pendingChanges?.appliesAt;
  const showPausedAlert = status === 'paused' && isActive && pendingChanges?.appliesAt;
  const showInReviewAlert = status === 'inReview';
  const showNoCcAddedAlert = status === 'noCcAdded';
  const showOverbudgetAlert = spentToBudgetPercent >= BUDGET_SPENT_THRESHOLD_80;
  const showAlertBoxes =
    showDeactivatedAlert ||
    showRunningAlert ||
    showPausedAlert ||
    showInReviewAlert ||
    showOverbudgetAlert ||
    showNoCcAddedAlert;

  return (
    <>
      {showAlertBoxes && (
        <div
          className="mt-4 flex flex-col gap-4"
          {...getDataAutoTestAttributes(['campaign-status-message']).attributes}
        >
          {showDeactivatedAlert && (
            <AlertBox
              className="border-red-100"
              iconClass="text-red-500"
              iconName="error_outline"
            >
              <div className="text-sm">
                <div className="font-medium">Campaign is deactivated</div>
                <div className="mt-2 text-slate-700">
                  {isSales
                    ? 'This campaign is not receiving any leads. You can contact your partnerships manager for details.'
                    : 'Campaign is not receiving any leads or appointments. You can contact your partnerships manager for details.'}
                </div>
              </div>
            </AlertBox>
          )}
          {showRunningAlert && (
            <AlertBox
              className="border-orange-100"
              iconClass="text-orange-500"
              iconName="update"
            >
              <div className="text-sm text-slate-700">
                You will stop receiving leads or appointments on {formattedAppliesAt}.
                <div className="mt-2 hidden">
                  {/* TODO: href is incorrect, replace on correct */}
                  <a
                    href="/"
                    className="font-semibold underline"
                  >
                    Learn more
                  </a>
                </div>
              </div>
            </AlertBox>
          )}
          {showPausedAlert && (
            <AlertBox
              className="border-orange-100"
              iconClass="text-orange-500"
              iconName="update"
            >
              <div className="text-sm text-slate-700">
                You will start receiving leads or appointments on {formattedAppliesAt}.
                <div className="mt-2 hidden text-slate-700">
                  {/* TODO: href is incorrect, replace on correct */}
                  <a
                    href="/"
                    className="font-semibold underline"
                  >
                    Learn more
                  </a>
                </div>
              </div>
            </AlertBox>
          )}
          {showInReviewAlert && (
            <AlertBox
              className="border-deep-blue-100"
              iconClass="text-deep-blue-500"
              iconName="info"
            >
              <div className="mb-2 font-medium">This campaign is under moderation</div>
              <div className="text-slate-700">
                An partnerships manager will review details and approve the campaign.
              </div>
            </AlertBox>
          )}
          {showNoCcAddedAlert && (
            <AlertBox
              className="border-orange-100"
              iconClass="text-orange-500"
              iconName="warning_amber"
            >
              <div className="text-sm">
                <div className="font-medium">Campaign will not run until you add a Credit Card</div>
                <div className="mt-2 text-slate-700">
                  Your card will be charged once you begin receiving leads and/or appointments.
                </div>
              </div>
            </AlertBox>
          )}
          {showOverbudgetAlert && (
            <OverbudgetAlert
              alertText={alertText}
              showIncreaseBudgetBtnBtn
            />
          )}
        </div>
      )}
    </>
  );
}
